/** The mime type (or media type) is only used internally, so using the "x." sub type is OK. */
export const CMMIPS_MIMETYPE = "image/x.cmmips";
const CMMIPS_MAGIC = 0xee77;
/** Set to true to get verbose CmMips parsing info to the console. */
const CMMIPS_VERBOSE = false;
/**
 * Extracts the specified MIP level image data from the CmMips container.
 *
 * By default the highest quality MIP level is loaded. The optional maxResolution setting will
 * instead load the highest quality MIP level where BOTH width and height is < maxResolution, or
 * the lowest quality if none fulfill the requirement.
 *
 * Returns a filled in CmMips class with meta data as well as the byte array with the image data.
 * Returns undefined if there was an error
 *
 * @warning: For efficacy and maximum flexibility, the returned Uint8Array is only a new View into
 * the supplied bytes typed array. It is up to the caller to call slice() on it to create a copy of
 * the data if needed.
 */
export function extractCmMips(bytes, logger, maxRes) {
    try {
        if (bytes === undefined || bytes.length < 6) {
            logger.error("(CmMips) Input array undefined or much too short", bytes === null || bytes === void 0 ? void 0 : bytes.length);
            return undefined;
        }
        const stream = new CmMipsStream(bytes);
        const magic = stream.readUint16();
        if (magic !== CMMIPS_MAGIC) {
            logger.error("(CmMips) Incorrect magic.", `Expected ${CMMIPS_MAGIC} got ${magic}`);
            return undefined;
        }
        const version = stream.readUint32();
        const suffix = stream.readString();
        const format = stream.readString();
        const averageR = stream.readUint8();
        const averageG = stream.readUint8();
        const averageB = stream.readUint8();
        const minWidth = stream.readUint32();
        const minHeight = stream.readUint32();
        const mipLevels = stream.readUint32();
        if (CMMIPS_VERBOSE) {
            logger.info("CmMips header data", `\nMagic: ${magic}`, `\nVersion: ${version}`, `\nSuffix: ${suffix}`, `\nFormat: ${format}`, `\nAverage color: (${averageR}, ${averageG}, ${averageB})`, `\nMip levels: ${mipLevels}`, `\nSmallest: (${minWidth}, ${minHeight})`, `\n---`, `\nLargest: (${minWidth << (mipLevels - 1)}, ${minHeight << (mipLevels - 1)})`, `\nMaxResolution: ${maxRes === undefined ? "None" : maxRes}`);
        }
        // Chunk start position. Byte offset into buffer, or -1 if external
        let pos = 0;
        let index = -1;
        let width = 0;
        let height = 0;
        for (let i = 0; i < mipLevels; i++) {
            const value = stream.readUint32();
            if (value === 0xffffffff) {
                // This Mip is external, skip
                continue;
            }
            const w = minWidth << i;
            const h = minHeight << i;
            if (index === -1 || maxRes === undefined || (w <= maxRes && h <= maxRes)) {
                // First internal Mip found, or Mip has OK dimensions
                pos = value;
                index = i;
                width = w;
                height = h;
            }
        }
        if (index === -1) {
            logger.warn("(CmMips) No internal Mip found");
            return undefined;
        }
        // Chunk size. Byte length of each chunk in the buffer
        let size = 0;
        let maxSize = 0;
        let totalSize = 0;
        for (let i = 0; i < mipLevels; i++) {
            const value = stream.readUint32();
            totalSize += value;
            maxSize = Math.max(maxSize, value);
            if (i === index) {
                size = value;
            }
        }
        // Chunk names (SHA-256)
        let name = "";
        for (let i = 0; i < mipLevels; i++) {
            if (i === index) {
                name = stream.readString();
                // No need to read further since this is last in the header
                break;
            }
            else {
                stream.skipString();
            }
        }
        if (name.length === 0 || size === 0 || pos === 0 || pos + size > bytes.length) {
            logger.error("(CmMips) Chunk meta data error.", `Name:"${name}", pos:${pos}, size:${size}, end:${bytes.length}`);
            return undefined;
        }
        if (CMMIPS_VERBOSE) {
            logger.info("(CmMips) Mip info", `\nLoaded: (${width}, ${height})`, `\nChunk name: ${name}`, `\nChunk size: ${size}`, `\nChunk pos: ${pos}`, `\n---`, `\nTotal size: ${totalSize >> 10} kb`, `\nMax size: ${maxSize >> 10} kb`, `\nOverhead: ${Math.round((100 * (totalSize - maxSize)) / totalSize)}%`);
        }
        // Using subarray(...) means that we simply return another view of underlying ArrayBuffer,
        // not actually copying any data. This is intentional.
        return new CmMip(name, suffix, width, height, bytes.subarray(pos, pos + size));
    }
    catch (e) {
        logger.error("(CmMips) Caught exception: " + e);
        return undefined;
    }
}
export class CmMip {
    constructor(name, suffix, width, height, data) {
        this.name = name;
        this.suffix = suffix;
        this.width = width;
        this.height = height;
        this.data = data;
    }
}
/**
 * Helper class for reading the serialized CmMips header.
 *
 * The string format is from CM / DEX (?) and called "TinyNanoCStr".
 * Numbers are stored in little endian.
 */
class CmMipsStream {
    constructor(bytes) {
        this.pos = 0;
        this.bytes = bytes;
        this.view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    }
    readUint8() {
        const value = this.view.getUint8(this.pos);
        this.pos += 1;
        return value;
    }
    readUint16() {
        const value = this.view.getUint16(this.pos, true);
        this.pos += 2;
        return value;
    }
    readUint32() {
        const value = this.view.getUint32(this.pos, true);
        this.pos += 4;
        return value;
    }
    skipString() {
        const length = this.view.getUint8(this.pos);
        this.pos += 1 + length;
    }
    readString() {
        if (this.textDecoder === undefined) {
            this.textDecoder = new TextDecoder("utf-8");
        }
        const length = this.view.getUint8(this.pos);
        this.pos += 1;
        const text = this.textDecoder.decode(this.bytes.subarray(this.pos, this.pos + length));
        this.pos += length;
        return text;
    }
}
